import React, { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { BarChart, ResponsiveContainer, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";
import { useTranslation } from "react-i18next";
import { useService } from "../../../dashboard/hook";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { getRemoteUtilityId } from '@shared/utils/loginUtils';
import { useConsumerConsumptionKpi, useConsumerMonthlyConsumption } from '../hook';

interface ConsumptionTabProps {
  consumerNo?: string;
  consumerId?: string;
}

export function ConsumptionTab({ consumerNo, consumerId }: ConsumptionTabProps) {
  const { t } = useTranslation();

  const [utilityFilter, setUtilityFilter] = useState("");

  const { data: serviceData } = useService({
    consumer: consumerId,
  });

  const { data: summary } = useConsumerConsumptionKpi({
    consumer_no: consumerNo || '',
    remote_utility_id: getRemoteUtilityId(),
    utility_service: utilityFilter,
  });

  const {data:monthlyConsumption}=useConsumerMonthlyConsumption({
    remote_utility_id: getRemoteUtilityId(),
    consumer_no: consumerNo || '',
    no_of_months:6,
    utility_service: utilityFilter,
  });

  const activeServices = useMemo(
    () => serviceData?.result || [],
    [serviceData]
  );


  useEffect(() => {
    if (activeServices.length > 0 && !utilityFilter) {
      setUtilityFilter(activeServices[0]);
    }
  }, [activeServices, utilityFilter]);

  const transformedUsageData = useMemo(() => {
    if (!monthlyConsumption) return [];
    
    return monthlyConsumption.map((item: any) => ({
      name: item.month,
      consumption: item.value,
    }));
  }, [monthlyConsumption]);


  return (
    <div className="py-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex flex-col justify-center">
          <p className="text-[11px] font-bold text-gray-400 mb-1">{t("Current Month")}</p>
          <p className="font-bold text-xl text-blue-600">{summary?.currentMonth} Unit</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex flex-col justify-center">
          <p className="text-[11px] font-bold text-gray-400 mb-1">{t("6-Month Average")}</p>
          <p className="font-bold text-xl text-blue-600">{summary?.sixMonthAverage} Unit</p>
        </div>
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm flex flex-col justify-center">
          <p className="text-[11px] font-bold text-gray-400 mb-1">{t("Peak Month")}</p>
          <p className="font-bold text-xl text-blue-600">{summary?.peakMonth}</p>
        </div>
      </div>
      <Card className="rounded-2xl shadow-sm border-gray-100 bg-white">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-[14px] font-bold text-gray-800">{t("Monthly Consumption (6 Months)")}</CardTitle>
          {activeServices.length > 0 && (
            <Select value={utilityFilter} onValueChange={setUtilityFilter}>
              <SelectTrigger className="w-[180px] h-9 text-xs font-medium">
                <SelectValue placeholder={t("Select utility service")} />
              </SelectTrigger>
              <SelectContent>
                {activeServices.map((service: string, index: number) => (
                  <SelectItem key={index} value={service} className="text-xs">
                    {service}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </CardHeader>
        <CardContent className="px-2">
          <div className="h-[280px] w-full">
            {transformedUsageData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={transformedUsageData}
                  margin={{ top: 20, right: 30, left: -20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={{ stroke: '#e5e7eb' }} 
                    tickLine={false} 
                    tick={{ fontSize: 11, fill: '#9ca3af', fontWeight: 500 }} 
                    dy={10} 
                  />
                  <YAxis 
                    axisLine={{ stroke: '#e5e7eb' }} 
                    tickLine={false} 
                    tick={{ fontSize: 11, fill: '#9ca3af', fontWeight: 500 }} 
                    dx={-10} 
                    ticks={[0, 4, 8, 12, 16]}
                    domain={[0, 16]}
                  />
                  <Tooltip 
                    cursor={{ fill: 'transparent' }} 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', fontSize: '13px', fontWeight: 600 }} 
                  />
                  <Bar
                    dataKey="consumption"
                    fill="#3b82f6"
                    radius={[4, 4, 0, 0]}
                    barSize={100}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-gray-400 text-sm">
                {utilityFilter ? t("no usage data available...") : t("Select a utility service to view data")}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
